import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';

export default function ImportPage() {
  const [dateien, setDateien] = useState([]);
  const [bank, setBank] = useState('auto');
  const [loading, setLoading] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);
  const [fehler, setFehler] = useState('');
  const [imports, setImports] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [fortschritt, setFortschritt] = useState(null); // { index, total, name }
  const fileRef = useRef();

  useEffect(() => { ladeImports(); }, []);

  async function ladeImports() {
    const r = await api.get('/imports');
    setImports(r.data);
  }

  const onDrop = useCallback(e => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f =>
      f.name.toLowerCase().endsWith('.csv') || f.name.toLowerCase().endsWith('.pdf')
    );
    setDateien(prev => [...prev, ...files]);
  }, []);

  function onDateiWahl(e) {
    const files = Array.from(e.target.files);
    setDateien(prev => [...prev, ...files]);
  }

  function dateiEntfernen(idx) {
    setDateien(prev => prev.filter((_, i) => i !== idx));
  }

  async function hochladen() {
    if (dateien.length === 0) return;
    setLoading(true);
    setFehler('');
    setErgebnis(null);

    const ergebnisse = [];
    let gesamtImportiert = 0;
    let gesamtDuplikate = 0;

    for (let i = 0; i < dateien.length; i++) {
      const datei = dateien[i];
      setFortschritt({ index: i + 1, total: dateien.length, name: datei.name });

      try {
        const form = new FormData();
        form.append('dateien', datei);
        form.append('bank', bank);
        const r = await api.post('/upload', form, { timeout: 0 });
        const df = r.data.dateien?.[0];
        ergebnisse.push(df || { datei: datei.name, importiert: r.data.importiert, gesamt: r.data.gesamt });
        gesamtImportiert += r.data.importiert || 0;
        gesamtDuplikate += r.data.duplikate || 0;
      } catch (e) {
        const isNetzwerk = !e.response;
        ergebnisse.push({
          datei: datei.name,
          fehler: isNetzwerk
            ? 'Verbindungsabbruch – Import läuft möglicherweise noch im Hintergrund. Import-Historie prüfen!'
            : (e.response?.data?.error || 'Upload fehlgeschlagen')
        });
      }
    }

    setFortschritt(null);
    setErgebnis({ importiert: gesamtImportiert, duplikate: gesamtDuplikate, dateien: ergebnisse });
    setDateien([]);
    ladeImports();
    setLoading(false);
  }

  const csvCount = dateien.filter(d => d.name.toLowerCase().endsWith('.csv')).length;
  const pdfCount = dateien.filter(d => d.name.toLowerCase().endsWith('.pdf')).length;

  return (
    <div>
      <h1 style={{ margin: '0 0 20px', fontSize: '20px', color: '#1e293b' }}>📤 Kontoauszüge importieren</h1>

      <div style={{ background: 'white', borderRadius: '14px', padding: '20px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '600' }}>Bank / Format</label>
          <select value={bank} onChange={e => setBank(e.target.value)}
            style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '2px solid #e2e8f0', fontSize: '15px' }}>
            <option value="auto">🔍 Automatisch erkennen</option>
            <option value="ING">🟠 ING (CSV oder PDF)</option>
            <option value="Commerzbank">🟡 Commerzbank (CSV)</option>
            <option value="C24">🔵 C24 Bank (CSV)</option>
          </select>
        </div>

        {/* Drag & Drop Zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current.click()}
          style={{
            border: '2px dashed ' + (dragOver ? '#6366f1' : '#e2e8f0'),
            borderRadius: '12px',
            padding: '32px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragOver ? '#eff6ff' : '#f8fafc',
            transition: 'all 0.2s',
            marginBottom: '14px'
          }}>
          <div style={{ fontSize: '40px', marginBottom: '8px' }}>{dragOver ? '📂' : '📁'}</div>
          <div style={{ fontWeight: '700', color: '#374151', marginBottom: '4px' }}>
            {dragOver ? 'Dateien loslassen!' : 'Dateien hierher ziehen'}
          </div>
          <div style={{ color: '#94a3b8', fontSize: '13px' }}>oder klicken zum Auswählen</div>
          <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '6px' }}>CSV und PDF · Mehrere Dateien möglich</div>
          <input ref={fileRef} type="file" multiple accept=".csv,.CSV,.pdf,.PDF"
            onChange={onDateiWahl} style={{ display: 'none' }} />
        </div>

        {/* Dateiliste */}
        {dateien.length > 0 && (
          <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>
              {dateien.length} Datei{dateien.length > 1 ? 'en' : ''} ausgewählt
              {csvCount > 0 && ` · ${csvCount} CSV`}
              {pdfCount > 0 && ` · ${pdfCount} PDF`}
            </div>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {dateien.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#f8fafc', borderRadius: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '13px', color: '#374151' }}>
                    {d.name.toLowerCase().endsWith('.pdf') ? '📄' : '📊'} {d.name}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>{(d.size / 1024).toFixed(0)} KB</span>
                    <button onClick={e => { e.stopPropagation(); dateiEntfernen(i); }}
                      style={{ background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '6px', padding: '2px 8px', cursor: 'pointer', fontSize: '14px' }}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fortschrittsanzeige */}
        {fortschritt && (
          <div style={{ marginBottom: '14px', background: '#eff6ff', borderRadius: '10px', padding: '12px 16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#3730a3', marginBottom: '4px' }}>
              ⏳ Datei {fortschritt.index} von {fortschritt.total}
            </div>
            <div style={{ fontSize: '12px', color: '#4f46e5' }}>
              {fortschritt.name}
            </div>
            <div style={{ fontSize: '11px', color: '#6366f1', marginTop: '4px' }}>
              Parsen, KI-Analyse und Import – bitte warten…
            </div>
          </div>
        )}

        <button onClick={hochladen} disabled={dateien.length === 0 || loading}
          style={{ width: '100%', padding: '14px', background: loading || dateien.length === 0 ? '#94a3b8' : 'linear-gradient(135deg,#6366f1,#4f46e5)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: dateien.length > 0 && !loading ? 'pointer' : 'default' }}>
          {loading
            ? `⏳ Importiere ${fortschritt ? `${fortschritt.index}/${fortschritt.total}` : ''}…`
            : `📤 ${dateien.length > 0 ? dateien.length + ' Datei' + (dateien.length > 1 ? 'en' : '') + ' importieren' : 'Dateien auswählen'}`}
        </button>

        {ergebnis && (
          <div style={{ marginTop: '16px', background: '#dcfce7', borderRadius: '10px', padding: '14px', color: '#15803d' }}>
            <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '8px' }}>✅ Import abgeschlossen!</div>
            <div>📊 {ergebnis.importiert} neue Buchungen importiert</div>
            <div>🔄 {ergebnis.duplikate} Duplikate übersprungen</div>
            {ergebnis.dateien && (
              <div style={{ marginTop: '8px' }}>
                {ergebnis.dateien.map((d, i) => (
                  <div key={i} style={{ fontSize: '12px', marginTop: '4px', color: d.fehler ? '#dc2626' : '#15803d' }}>
                    {d.fehler ? '❌' : '✅'} {d.datei}:{' '}
                    {d.fehler
                      ? d.fehler
                      : d.importiert != null
                        ? `${d.importiert} neue Buchungen${d.gesamt && d.gesamt > d.importiert ? ` (${d.gesamt - d.importiert} Duplikate)` : ''}`
                        : 'importiert'}
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: '8px', color: '#64748b', fontSize: '13px' }}>KI hat Kategorien vorgeschlagen → in "Buchungen" bestätigen</div>
          </div>
        )}

        {fehler && (
          <div style={{ marginTop: '16px', background: '#fee2e2', borderRadius: '10px', padding: '14px', color: '#dc2626' }}>
            ❌ {fehler}
          </div>
        )}
      </div>

      <div style={{ background: 'white', borderRadius: '14px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <h3 style={{ margin: '0 0 12px', color: '#1e293b' }}>Import-Historie</h3>
        {imports.length === 0 && <p style={{ color: '#94a3b8', textAlign: 'center' }}>Noch keine Imports</p>}
        {imports.map(imp => {
          const neueBuchungen = imp.anzahl ?? 0;
          const dups = imp.duplikate ?? 0;
          const gesamt = imp.gesamt ?? neueBuchungen;
          const allesDups = neueBuchungen === 0 && dups > 0;
          return (
            <div key={imp.id} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: '600', color: '#374151', fontSize: '13px' }}>
                  {imp.dateiname?.toLowerCase().endsWith('.pdf') ? '📄' : '📊'} {imp.dateiname}
                </span>
                <span style={{ fontWeight: '700', fontSize: '13px', color: allesDups ? '#94a3b8' : '#10b981' }}>
                  {allesDups
                    ? `${dups} Duplikate`
                    : `${neueBuchungen} Buchungen`}
                </span>
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                {imp.bank} · {imp.von_datum || '—'} bis {imp.bis_datum || '—'} · {new Date(imp.created_at).toLocaleDateString('de-DE')}
                {!allesDups && dups > 0 && ` · ${dups} Duplikate übersprungen`}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
